import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AutomataContextService,
  AUTOMATION_IS_USER_SUPERADMIN$,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  STANDARD_DIALOG_WIDTH,
  STANDARD_DIALOG_WIDTH_NUMBER,
} from '@galaxy/automata/shared';
import { TranslateService } from '@ngx-translate/core';
import {
  AutomataDefaultAutomationsService,
  AutomataService,
  Automation,
  AutomationConfigurationV2,
  AutomationInterface,
  AutomationSettingsInterface,
  AutomationType,
  Context,
  EntityType,
  FieldMaskInterface,
  IsAutomationAPublishedDefaultAutomation,
  IsAutomationDraining,
  Oauth2Action,
  RunningState,
  RunType,
} from '@vendasta/automata';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getFieldViolationsFromError } from '@vendasta/shared';
import moment from 'moment-timezone';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { initialTemplateCreationQueryParam } from '../common/constants';
import { PublishBuiltInAutomationDialogComponent } from '../dialogs/publish-built-in-automation/publish-built-in-automation-dialog.component';
import {
  TurnOffAutomationDialogComponent,
  TurnOffDialogResult,
} from '../dialogs/turn-off-automation/turn-off-automation-dialog.component';
import { DialogResult as TurnOnDialogResult, TurnOnAutomationDialogComponent } from '../dialogs/turn-on-automation';
import { UnpublishBuiltInAutomationDialogComponent } from '../dialogs/unpublish-built-in-automation/unpublish-built-in-automation-dialog.component';
import { AutomationsRouterService } from '../shared/automations-router.service';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';

export interface AutomationCreateInterface {
  name: string;
  id: string;
}

interface BuiltInToggleInterface {
  partnerId: string;
}

/* AutomationService
 * Is a stateless service that contains functionality related to an automation. It needs to be told what the type of automation
 * is so that it can hit the correct api service function.
 * This exists so that this functionality can be used outside of being a child of the AutomationComponent
 */
@Injectable({ providedIn: 'root' })
export class AutomationService {
  automationContext = this.automationContextService.currentContext;
  confirmationModal: OpenConfirmationModalService;

  constructor(
    private readonly translateService: TranslateService,
    private readonly automataService: AutomataService,
    private readonly automataBuiltinsService: AutomataDefaultAutomationsService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
    private readonly router: AutomationsRouterService,
    private readonly snackbarService: SnackbarService,
    private dialog: MatDialog,
    private readonly automationContextService: AutomataContextService,
    private readonly openConfirmationModalService: OpenConfirmationModalService,
    @Inject(AUTOMATION_IS_USER_SUPERADMIN$) public readonly isUserSuperAdmin$: Observable<boolean>,
  ) {
    this.confirmationModal = openConfirmationModalService;
  }

  public createFromSource(
    source?: AutomationCreateInterface,
    entityType?: EntityType,
    skipNavigation?: boolean,
  ): Observable<string> {
    let createCall$: Observable<string>;
    if (source?.id) {
      const name = source?.name
        ? this.translateService.instant('AUTOMATIONS.COMMON.DUPLICATE_OF_NAME', { name: source?.name })
        : this.translateService.instant('AUTOMATIONS.CREATE.UNTITLED');
      createCall$ = this.namespace$.pipe(
        switchMap((namespace) =>
          this.automataService.createAutomation(
            {
              namespace: namespace,
              name: name,
              context: this.automationContext,
              settings: { timeZone: this.getTimeZone() },
            },
            source.id,
          ),
        ),
      );
    } else {
      createCall$ = this.namespace$.pipe(
        switchMap((namespace) =>
          this.automataService.createAutomation({
            namespace: namespace,
            name: this.translateService.instant('AUTOMATIONS.CREATE.UNTITLED'),
            entityType: entityType || EntityType.ENTITY_TYPE_NO_ENTITY,
            context: this.automationContext,
            settings: {
              runType: RunType.RUN_TYPE_MULTIPLE,
              timeZone: this.getTimeZone(),
            },
          }),
        ),
      );
    }
    return createCall$.pipe(
      catchError((err) => {
        const fieldViolations = getFieldViolationsFromError(err);
        const nameFieldError = fieldViolations?.find((fv) => fv.field === 'Automation.WorkflowStep');
        if (!nameFieldError) {
          this.snackbarService.openErrorSnack(this.translateService.instant('COMMON.ERROR_TRY_AGAIN'));
        }
        return throwError(() => err);
      }),
      take(1),
      tap((id) => {
        if (id && !skipNavigation) {
          this.router.navigate(['automations', 'automation', id, 'workflow'], {
            queryParams: { [initialTemplateCreationQueryParam]: true },
          });
        }
      }),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteAutomation(a: Automation, type: AutomationType): Observable<boolean> {
    return this.confirmationModal
      .openModal({
        type: 'warn',
        title: this.translateService.instant('AUTOMATIONS.DELETE_DIALOG.TITLE', {
          automationName: a.name,
        }),
        message: 'AUTOMATIONS.DELETE_DIALOG.MESSAGE',
        confirmButtonText: 'AUTOMATIONS.DELETE_DIALOG.CONFIRM',
        cancelButtonText: 'AUTOMATIONS.DELETE_DIALOG.DECLINE',
        width: STANDARD_DIALOG_WIDTH_NUMBER,
      })
      .pipe(
        take(1),
        switchMap((confirmed) => {
          if (confirmed) {
            return this.automataService.deleteAutomation(a.id).pipe(
              take(1),
              catchError((err) => {
                this.snackbarService.errorSnack(
                  this.translateService.instant('AUTOMATIONS.ERRORS.AUTOMATIONS.DELETING'),
                );
                return throwError(err);
              }),
            );
          } else {
            return of(false);
          }
        }),
        tap((success) => {
          if (success) {
            this.snackbarService.successSnack(this.translateService.instant('AUTOMATIONS.SUCCESS.DELETED'));
          }
        }),
      );
  }

  copyAutomation(a: Automation, type: AutomationType): Observable<string> {
    if (type === AutomationType.AUTOMATION_TYPE_DEFAULT || type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      console.error('tried to copy an automation type that is not supported yet ' + type);
      return of('');
    }
    return this.automataService
      .copyAutomation(a.id, this.translateService.instant('AUTOMATIONS.COMMON.DUPLICATE_OF_NAME', { name: a.name }))
      .pipe(
        take(1),
        catchError((err) => {
          console.error(err);
          this.snackbarService.errorSnack(this.translateService.instant('AUTOMATIONS.ERRORS.AUTOMATIONS.DUPLICATING'));
          return throwError(err);
        }),
      );
  }

  openTurnOnDialog(a: Automation, type: AutomationType): Observable<TurnOnDialogResult | null> {
    let dialog: any = TurnOnAutomationDialogComponent;
    let data: any = {
      automation: a,
    };
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.openPublishTemplateDialog(a);
    } else if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      dialog = PublishBuiltInAutomationDialogComponent;
      data = {
        builtInAutomation: a,
      };
    }
    const turnOnDialogRef = this.dialog.open(dialog, {
      width: STANDARD_DIALOG_WIDTH,
      data: data,
    });
    return turnOnDialogRef.afterClosed();
  }

  turnOn(
    id: string,
    type: AutomationType,
    settings?: AutomationSettingsInterface,
    builtInToggle?: BuiltInToggleInterface,
    subscribeToNotifications?: boolean,
  ): Observable<string> {
    if (builtInToggle) {
      return this.automataBuiltinsService
        .enable(id, this.automationContext, builtInToggle.partnerId)
        .pipe(map(() => ''));
    }
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.automataService.publish(id).pipe(map(() => ''));
    }
    if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      return this.automataService.startAutomationRunning(id, settings, subscribeToNotifications).pipe(map(() => ''));
    }

    return this.isUserSuperAdmin$.pipe(
      take(1),
      map((isSuperadmin) =>
        this.automataService.getOauth2EntryHandlerUrl(
          id,
          Oauth2Action.Start,
          window.location.href,
          settings,
          subscribeToNotifications,
          isSuperadmin,
        ),
      ),
    );
  }

  afterOnAction(a: Automation): void {
    // a default automation is a built-in automation
    if (!a.isTemplate && !a.isDefaultAutomation) {
      this.router.navigate([`/automations/automation/${a.id}/activity`]);
    }
  }

  openTurnOffDialog(a: Automation, type: AutomationType, isPartner?: boolean): Observable<TurnOffDialogResult | null> {
    let dialog: any = TurnOffAutomationDialogComponent;
    let data: any = {
      automation: a,
    };
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.openUnpublishTemplateDialog(a);
    } else if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      if (isPartner) {
        return this.openTurnOffSystemAutomationDialog(a);
      } else {
        dialog = UnpublishBuiltInAutomationDialogComponent;
        data = {
          builtInAutomation: a,
        };
      }
    }
    const turnOffDialogRef = this.dialog.open(dialog, {
      width: STANDARD_DIALOG_WIDTH,
      data: data,
    });
    return turnOffDialogRef.afterClosed();
  }

  private openUnpublishTemplateDialog(template: Automation): Observable<TurnOffDialogResult | null> {
    return this.openConfirmationModalService
      .openModal({
        type: 'confirm',
        title: this.translateService.instant('AUTOMATIONS.UNPUBLISH_TEMPLATE_DIALOG.TITLE', { name: template.name }),
        message: this.translateService.instant('AUTOMATIONS.UNPUBLISH_TEMPLATE_DIALOG.DESCRIPTION'),
      })
      .pipe(
        take(1),
        map((clickedYes) => {
          return clickedYes
            ? {
                automationId: template.id,
              }
            : null;
        }),
      );
  }

  private openTurnOffSystemAutomationDialog(a: Automation): Observable<TurnOffDialogResult | null> {
    return this.openConfirmationModalService
      .openModal({
        type: 'warn',
        title: this.translateService.instant('AUTOMATIONS.TURN_OFF_AUTOMATION_DIALOG.TITLE', {
          name: a.name,
        }),
        confirmButtonText: 'AUTOMATIONS.TURN_OFF_AUTOMATION_DIALOG.TURN_OFF',
      })
      .pipe(
        take(1),
        map((confirmed) => {
          return confirmed
            ? {
                automationId: a.id,
              }
            : null;
        }),
      );
  }

  private openPublishTemplateDialog(template: Automation): Observable<TurnOnDialogResult | null> {
    return this.openConfirmationModalService
      .openModal({
        type: 'confirm',
        title: this.translateService.instant('AUTOMATIONS.PUBLISH_TEMPLATE_DIALOG.TITLE', { name: template.name }),
        message: this.translateService.instant('AUTOMATIONS.PUBLISH_TEMPLATE_DIALOG.DESCRIPTION'),
      })
      .pipe(
        take(1),
        map((clickedYes) => {
          return clickedYes
            ? {
                automationId: template.id,
                subscribe: false,
              }
            : null;
        }),
      );
  }

  turnOff(
    id: string,
    type: AutomationType,
    builtInToggle?: BuiltInToggleInterface,
    runningState?: RunningState,
  ): Observable<boolean> {
    if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      if (builtInToggle) {
        return this.automataBuiltinsService.disable(id, this.automationContext, builtInToggle.partnerId);
      }
      return this.automataService.stopAutomationRunning(id);
    }
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.automataService.unpublish(id);
    }
    if (runningState === RunningState.RUNNING_STATE_STOPPED) {
      return this.automataService.stopAutomationRunning(id);
    }
    if (runningState === RunningState.RUNNING_STATE_DRAINING) {
      return this.automataService.setAutomationToDraining(id);
    }
    // if this point is reached, an invalid running state was chosen
    return of(false);
  }

  getErrorTurningOnMessage(a: Automation, hasErrors: boolean, err: HttpErrorResponse): string {
    let key = '';
    if (a.isTemplate) {
      if (err.status === 412) {
        key = 'AUTOMATIONS.ERRORS.TEMPLATES.PUBLISHING_LIMIT_REACHED';
      } else {
        key = 'AUTOMATIONS.ERRORS.TEMPLATES.PUBLISHING';
        if (hasErrors) {
          key = 'AUTOMATIONS.ERRORS.TEMPLATES.PUBLISHING_WITH_ERRORS';
        }
      }
    } else if (a.isDefaultAutomation) {
      key = 'AUTOMATIONS.ERRORS.MANAGE_BUILT_IN_AUTOMATIONS.PUBLISHING';
      if (hasErrors) {
        key = 'AUTOMATIONS.ERRORS.MANAGE_BUILT_IN_AUTOMATIONS.PUBLISHING_WITH_ERRORS';
      }
    } else {
      key = 'AUTOMATIONS.ERRORS.AUTOMATIONS.STARTING';
      if (hasErrors) {
        key = 'AUTOMATIONS.ERRORS.AUTOMATIONS.STARTING_WITH_ERRORS';
      }
    }
    return this.translateService.instant(key);
  }

  getErrorTurningOffMessage(a: Automation): string {
    let key = 'AUTOMATIONS.ERRORS.AUTOMATIONS.STOPPING';
    if (a.isTemplate) {
      key = 'AUTOMATIONS.ERRORS.TEMPLATES.UNPUBLISHING';
    }
    // a default automation is a built-in automation
    if (a.isDefaultAutomation) {
      key = 'AUTOMATIONS.ERRORS.MANAGE_BUILT_IN_AUTOMATIONS.UNPUBLISHING';
    }
    return this.translateService.instant(key);
  }

  update(a: Automation, fieldMask: FieldMaskInterface): Observable<boolean> {
    return this.automataService.updateAutomation(a, fieldMask);
  }

  getStartEditingMessage(type: AutomationType): Observable<string> {
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.TEMPLATE.START_EDITING');
    }
    if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.BUILT_IN_AUTOMATION.START_EDITING');
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.AUTOMATION.START_EDITING');
  }

  getEditingPreventedWarning(automation: AutomationInterface, type: AutomationType): Observable<string> {
    if (type === AutomationType.AUTOMATION_TYPE_TEMPLATE) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.NO_EDITING_WHILE_TEMPLATE_PUBLISHED');
    }
    if (type === AutomationType.AUTOMATION_TYPE_DEFAULT) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.NO_EDITING_WHILE_BUILT_IN_AUTOMATION_PUBLISHED');
    }
    if (IsAutomationDraining(automation)) {
      return this.translateService.stream('AUTOMATIONS.EDITOR.NO_EDITING_WHILE_DRAINING');
    }
    return this.translateService.stream('AUTOMATIONS.EDITOR.NO_EDITING_WHILE_RUNNING');
  }

  getTags(partnerId: string, marketIds: string[], type: AutomationType): Observable<string[]> {
    return this.automataService.getTags(partnerId, marketIds, type).pipe(take(1));
  }

  getBuiltInConfiguration(
    namespace: string,
    automationContext: Context,
    automationId: string,
  ): Observable<AutomationConfigurationV2> {
    return this.automataBuiltinsService.getConfigurationV2(automationId, automationContext, namespace).pipe(take(1));
  }

  // returns whether the built in original automation is currently turned on for the partner/market
  // if no configuration is found for the partner/market it is considered turned on
  originBuiltInAutomationIsOn(
    namespace: string,
    automationContext: Context,
    originBuiltInAutomationId: string,
  ): Observable<boolean> {
    return this.getBuiltInConfiguration(namespace, automationContext, originBuiltInAutomationId).pipe(
      map((configuration) => {
        const isCurrentMarketDisabled = configuration?.disabled;
        return !isCurrentMarketDisabled;
      }),
      catchError((err) => {
        if (err.status === 404) {
          return of(true);
        }
        return of(false);
      }),
    );
  }

  // returns the built in automation if it has been published, otherwise null
  getOriginBuiltInAutomation(originBuiltInAutomationId: string): Observable<Automation> {
    return this.automataService.getAutomation(originBuiltInAutomationId).pipe(
      map((builtInAutomation) => {
        // if the built in is no longer published, don't refer to it
        if (IsAutomationAPublishedDefaultAutomation(builtInAutomation)) {
          builtInAutomation.name = builtInAutomation.name.trim();
        } else {
          builtInAutomation = null;
        }
        return builtInAutomation;
      }),
    );
  }

  // hasAutomationPermissions ensures the user has the Access Attributes on all the task definitions in this automation
  hasAutomationPermissions(automationId: string): Observable<boolean> {
    return this.automataService
      .getAutomationAccessRequirements(automationId)
      .pipe(map((accessRequirements) => (accessRequirements?.failedIds || []).length <= 0));
  }

  getTimeZone(): string {
    return moment.tz.guess();
  }

  subscribe(partnerId: string, marketIds: string[], automationId: string): Observable<boolean> {
    return this.automataService.subscribe(partnerId, marketIds, automationId).pipe(take(1));
  }

  unsubscribe(automationId: string): Observable<boolean> {
    return this.automataService.unsubscribe(automationId).pipe(take(1));
  }

  isSubscribed(automationId: string): Observable<boolean> {
    return this.automataService.getSubscription(automationId).pipe(
      take(1),
      map(() => {
        return true;
      }),
      catchError((err) => {
        if (err.status === 404) {
          return of(false);
        }
      }),
    );
  }
}
